// src/ui/FilterBar.jsx
import React, { useRef, useMemo, useCallback } from "react";
import { cx } from "../utils/cx";
import Spinner from "./Spinner";

/**
 * A reusable pill-style filter bar.
 * - Mutually exclusive selection (radio-group semantics)
 * - Horizontal scroll if overflow
 * - ArrowLeft/ArrowRight/Home/End keyboard navigation
 *
 * Props:
 * - items: Array<{ key, label, count? }>
 * - value: string (active key)
 * - onChange: (key) => void
 * - ariaLabel?: string
 * - outerClassName?: string           // outer frame (e.g., border-b bg-white)
 * - containerClassName?: string       // width container (e.g., max-w-3xl mx-auto px-6)
 * - navClassName?: string             // nav strip (e.g., flex ... py-2)
 * - pillClassName?: string            // extra classes for every pill
 * - activePillClassName?: string
 * - inactivePillClassName?: string
 * - badgeActiveClassName?: string
 * - badgeInactiveClassName?: string
 */
export default function FilterBar({
  items,
  value,
  onChange,
  ariaLabel = "Filters",
  outerClassName = "border-b border-border bg-surface-elevated",
  containerClassName = "w-full mx-auto px-6",
  navClassName = "flex justify-center gap-2 overflow-x-auto py-2",
  pillClassName = "inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm border border-border",
  activePillClassName = "bg-primary text-text-inverse border-primary",
  inactivePillClassName = "bg-surface-elevated text-text-secondary border hover:bg-surface",
  badgeActiveClassName = "bg-white/15 text-text-inverse",
  badgeInactiveClassName = "bg-surface-sunken text-text-secondary",
}) {
  const listRef = useRef(null);

  /**
   * Creates a map with items (filter options) as keys
   *    And either active or not active as the value
   */
  const indexByKey = useMemo(() => {
    const map = new Map();
    items?.forEach((it, idx) => map.set(it.key, idx));
    return map;
  }, [items]);

  const focusItem = useCallback((idx) => {
    /**
     * listref is a reference to the div, whose role is radiogroup
     *  Within the radio group, loops over all the items with role: "radio" -> the filter pills
     *  Returns the currently selected item (button)
     */
    const btn = listRef.current?.querySelectorAll('[role="radio"]')?.[idx];
    /**
     * focus() - gives keyboard focus so screen readers announce it
     *         - Gives the button a focus ring
     * preventScroll: true - prevents the browser's default scroll to bring selected element into view
     */
    btn?.focus({ preventScroll: true });
    /**
     * scrollIntoView - performs a minimal scroll to bring element into view within the scrollable container
     * "nearest" - scroll only if needed and only as much as needed
     */
    btn?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, []);

  const handleKeyDown = useCallback(
    (e) => {
      if (!items?.length) return;
      /**
       * Finds the index of the active filter
       */
      const cur = indexByKey.get(value) ?? 0;

      switch (e.key) {
        case "ArrowRight": {
          e.preventDefault();
          const next = (cur + 1) % items.length;
          onChange(items[next].key);
          focusItem(next);
          break;
        }
        case "ArrowLeft": {
          e.preventDefault();
          const prev = (cur - 1 + items.length) % items.length;
          onChange(items[prev].key);
          focusItem(prev);
          break;
        }
        case "Home": {
          e.preventDefault();
          onChange(items[0].key);
          focusItem(0);
          break;
        }
        case "End": {
          e.preventDefault();
          const last = items.length - 1;
          onChange(items[last].key);
          focusItem(last);
          break;
        }
        default:
          break;
      }
    },
    [items, indexByKey, value, onChange, focusItem]
  );

  return (
    <div className={outerClassName}>
      <div className={containerClassName}>
        <nav className={navClassName} aria-label={ariaLabel}>
          {/* Use radio-group semantics for mutually exclusive choices */}
          <div
            ref={listRef}
            role="radiogroup"
            aria-label={ariaLabel}
            className="flex gap-2"
            onKeyDown={handleKeyDown}
          >
            {items?.map((t) => {
              const isActive = t.key === value;
              return (
                <button
                  key={t.key}
                  type="button"
                  role="radio"
                  aria-checked={isActive}
                  aria-current={isActive ? "page" : undefined}
                  onClick={() => onChange(t.key)}
                  className={cx(
                    pillClassName,
                    isActive ? activePillClassName : inactivePillClassName,
                    "focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400"
                  )}
                >
                  {t.icon && (
                    <span className={cx("h-4 w-4 flex-shrink-0", isActive ? "text-text-inverse" : "text-text-secondary"
                    )}>
                      {t.icon}
                    </span>
                  )}
                  <span className={t.icon ? "hidden md:inline" : ""}>
                    {t.label}
                  </span>
                  {t.loading ? (
                    <span
                      className={cx(
                        "ml-1 inline-flex items-center justify-center h-5 w-5 rounded-full",
                        isActive ? "bg-white/15" : "bg-surface-sunken"
                      )}
                    >
                      <Spinner
                        size={12}
                        className={
                          isActive ? "text-text-inverse" : "text-text-muted"
                        }
                      />
                    </span>
                  ) : (
                    <span
                      className={cx(
                        "ml-1 inline-flex items-center justify-center h-5 px-1.5 rounded-full text-[11px]",
                        isActive ? badgeActiveClassName : badgeInactiveClassName
                      )}
                    >
                      {t.count ?? 0}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </nav>
      </div>
    </div>
  );
}
